import React, { useState } from 'react';
import { IconButton, Snackbar, Tooltip, CircularProgress } from '@mui/material';
import { Favorite, FavoriteBorder } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useFavorites } from '../context/FavoritesContext';
import { authService } from '../services/authService';
import { FavoriteCaregiverSummary } from '../services/favoritesService';

// FEAT-057 — Save / Favorite Caregivers (MVP-18.29). Heart toggle for caregiver
// discovery/search cards and the caregiver profile. Auth-gated: an
// unauthenticated tap routes to the auth page rather than calling the API.
// Toggling is optimistic (handled by FavoritesContext) with a revert + error
// snackbar on failure.

interface FavoriteButtonProps {
  caregiverId: string;
  summary?: FavoriteCaregiverSummary;
  size?: 'small' | 'medium' | 'large';
}

const FavoriteButton: React.FC<FavoriteButtonProps> = ({
  caregiverId,
  summary,
  size = 'medium',
}) => {
  const navigate = useNavigate();
  const { state, dispatch } = useApp();
  const { isFavorited, toggleFavorite } = useFavorites();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const favorited = isFavorited(caregiverId);
  const isAuthed = authService.isAuthenticated() && !!state.user;

  const handleClick = async (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();

    // Auth gate: prompt unauthenticated users to log in instead of calling the API.
    if (!isAuthed) {
      dispatch({ type: 'SET_REDIRECT_URL', payload: window.location.pathname });
      navigate('/auth');
      return;
    }

    if (pending) {
      return;
    }

    setPending(true);
    try {
      await toggleFavorite(caregiverId, summary);
    } catch {
      setError('Could not update favorites. Please try again.');
    } finally {
      setPending(false);
    }
  };

  const label = favorited ? 'Remove from favorites' : 'Save to favorites';

  return (
    <>
      <Tooltip title={label}>
        <span>
          <IconButton
            onClick={handleClick}
            size={size}
            color={favorited ? 'error' : 'default'}
            aria-label={label}
            aria-pressed={favorited}
            data-testid={`favorite-button-${caregiverId}`}
            disabled={pending}
          >
            {pending ? (
              <CircularProgress size={20} color="inherit" />
            ) : favorited ? (
              <Favorite />
            ) : (
              <FavoriteBorder />
            )}
          </IconButton>
        </span>
      </Tooltip>
      <Snackbar
        open={Boolean(error)}
        autoHideDuration={4000}
        onClose={() => setError(null)}
        message={error}
        data-testid="favorite-error-snackbar"
      />
    </>
  );
};

export default FavoriteButton;
